import React, { useEffect, useState } from "react";
import axios from "axios";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Label,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import useAppData from "@/store/useAppData";
import type { DeicingDashboardData } from "../../../service/Deicing/dashboardMetrics";
import type { ListScope } from "../ItemList/types";
import { periodRange, type Period } from "./periods";
import "./styles.css";

const number = (value: number | null | undefined, digits = 0) =>
    value == null ? "—" : value.toLocaleString("cs-CZ", { maximumFractionDigits: digits });
const today = () =>
    new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Prague",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(new Date());
const shift = (date: string, days: number) =>
    new Date(Date.parse(date) + days * 86400000).toISOString().slice(0, 10);
const dateLabel = (value: string) =>
    new Date(value).toLocaleDateString("cs-CZ", { timeZone: "UTC" });
const colors = ["#0e4169", "#218dc0", "#8ac5e0", "#c97015", "#f7b260", "#6d8c94"];

export default function DeicingDashboard({
    onChangedScope,
}: {
    onChangedScope: (scope: ListScope) => void;
}) {
    const moduleId = useAppData((state) => state.selectedModule?.id);
    const [period, setPeriod] = useState<Period>("month");
    const [from, setFrom] = useState(() => shift(today(), -29));
    const [to, setTo] = useState(today);
    const [forecast, setForecast] = useState<"recent" | "selected" | "all">("recent");
    const [data, setData] = useState<DeicingDashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [exportError, setExportError] = useState("");
    const [exporting, setExporting] = useState(false);
    const [revision, setRevision] = useState(0);
    const valid = Boolean(to && (period === "all" || (from && from <= to)));

    useEffect(() => {
        setData(null);
        if (!moduleId || !valid) return;
        const controller = new AbortController();
        setLoading(true);
        setError("");
        axios
            .post<DeicingDashboardData>(
                "api/deicing/getDashboard",
                { moduleId, dateFrom: period === "all" ? null : from, dateTo: to, forecast },
                { signal: controller.signal },
            )
            .then((result) => {
                if (!controller.signal.aborted) setData(result.data);
            })
            .catch(() => {
                if (!controller.signal.aborted)
                    setError("Manažerský přehled se nepodařilo načíst.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [moduleId, from, to, period, forecast, revision, valid]);

    const selectPeriod = (next: Period) => {
        setPeriod(next);
        if (next !== "custom") {
            const range = periodRange(next, today());
            setTo(range.to);
            setFrom(range.from);
        }
    };
    const exportExcel = async () => {
        if (!data) return;
        setExporting(true);
        setExportError("");
        try {
            const workbook = new Workbook();
            const summary = workbook.addWorksheet("Manažerský přehled");
            summary.addRows([
                ["Období", data.from, data.to],
                ["Aktualizováno", data.updatedAt],
                ["Počet odmrazů", data.total],
                ["Předchozí období", data.previous],
                ["Průměrná doba (min)", data.averageMinutes],
                ["Celkový čas (min)", data.totalMinutes],
                [
                    "Odhad – základ",
                    forecast === "recent"
                        ? "Posledních 30 dní"
                        : forecast === "selected"
                          ? "Vybrané období"
                          : "Celá doba",
                ],
                ["Odhad dalších odmrazů", data.forecast.remaining],
                ["Omezující zásoba", data.forecast.limiting],
                ["Vyčerpání", data.forecast.depletion],
                ["Objednat do (lhůta 14 dní)", data.forecast.orderBy],
                [],
                [
                    "Kapalina",
                    "Zásoba (l)",
                    "Příjem za 30 dní (l)",
                    "Odmrazy (l)",
                    "Odstřiky (l)",
                    "Celkem (l)",
                    "Průměr (l/odmraz)",
                    "Průměr za celou dobu",
                    "Odhad dalších odmrazů",
                ],
                ...data.fluids.map((fluid) => [
                    fluid.name,
                    fluid.quantity,
                    fluid.receipts,
                    fluid.consumption,
                    fluid.tech,
                    fluid.total,
                    fluid.average,
                    fluid.allAverage,
                    fluid.remaining,
                ]),
            ]);
            for (const [name, rows] of [
                ["Po dnech", data.daily.map((row) => [row.date, row.value])],
                ["Vozidla", data.vehicles.map((row) => [row.name, row.value])],
                ["Stojánky", data.stands.map((row) => [row.name, row.value])],
                ["Počasí", data.weather.map((row) => [row.name, row.value])],
            ] as [string, (string | number)[][]][]) {
                const sheet = workbook.addWorksheet(name);
                sheet.addRows([
                    [name === "Po dnech" ? "Datum" : "Název", "Počet odmrazů"],
                    ...rows,
                ]);
            }
            workbook.eachSheet((sheet) => {
                sheet.columns.forEach((column) => {
                    column.width = 28;
                });
                sheet.getRow(1).font = { bold: true };
            });
            saveAs(
                new Blob([await workbook.xlsx.writeBuffer()], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
                `deicing-prehled-${data.from}-${data.to}.xlsx`,
            );
        } catch {
            setExportError("Export do Excelu se nepodařil. Zkuste jej znovu.");
        } finally {
            setExporting(false);
        }
    };
    const ranking = (title: string, rows: { name: string; value: number }[], color: string) => (
        <section className="dd-panel">
            <h3>{title}</h3>
            {!rows.length ? (
                <p>Žádné odmrazy v období.</p>
            ) : (
                <>
                    <div style={{ height: Math.max(180, rows.length * 36) }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={rows}
                                layout="vertical"
                                margin={{ left: 0, right: 25 }}
                                accessibilityLayer
                            >
                                <XAxis type="number" allowDecimals={false} hide />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={105}
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    formatter={(value) => [number(Number(value)), "Počet odmrazů"]}
                                />
                                <Bar
                                    dataKey="value"
                                    fill={color}
                                    radius={[0, 4, 4, 0]}
                                    label={{ position: "right", fontSize: 12 }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <details>
                        <summary>Hodnoty a podíly</summary>
                        <ul>
                            {rows.map((row) => (
                                <li key={row.name}>
                                    {row.name}: {number(row.value)} (
                                    {number(data?.total ? (row.value / data.total) * 100 : 0, 1)} %)
                                </li>
                            ))}
                        </ul>
                    </details>
                </>
            )}
        </section>
    );
    const forecastFluids = data?.fluids.filter((fluid) => fluid.name !== "Voda") ?? [];
    const airlineRanking = (
        title: string,
        key: "fluid1" | "fluid2" | "water" | "value",
        color: string,
    ) => {
        const rows = (data?.airlines ?? [])
            .map((row) => ({ name: row.name, value: row[key] }))
            .filter((row) => key === "value" || row.value !== 0)
            .sort((a, b) => a.value - b.value || a.name.localeCompare(b.name, "cs"));
        const total = rows.reduce((sum, row) => sum + row.value, 0);
        const maximum = Math.max(1, ...rows.map((row) => row.value));
        const unit = key === "value" ? "" : " l";
        return (
            <section className="dd-panel">
                <h3>{title} podle leteckých společností</h3>
                {!rows.length ? <p>{key === "value" ? "Žádné odmrazy v období." : "Žádná spotřeba v období."}</p> : (
                    <ul className="dd-airline-ranking">
                        {rows.map((row) => (
                            <li key={row.name}>
                                <span className="dd-airline-name">{row.name}</span>
                                <span className="dd-airline-track" aria-hidden="true">
                                    <span style={{ width: `${row.value / maximum * 100}%`, background: color }} />
                                </span>
                                <span className="dd-airline-value">
                                    <b>{number(row.value, key === "value" ? 0 : 1)}{unit}</b>
                                    <small>{number(total ? row.value / total * 100 : 0, 1)} %</small>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        );
    };
    return (
        <>
            <nav className="item-list-toolbar dd-tabs" aria-label="Přehledy deicingu">
                <button type="button" className="item-list-tab" onClick={() => onChangedScope("all")}>
                    Seznam kapalin
                </button>
                <button type="button" className="item-list-tab" onClick={() => onChangedScope("allItems")}>
                    Odmrazy - Přehled
                </button>
                <button type="button" className="item-list-tab is-active" aria-current="page">
                    Statistika
                </button>
            </nav>
        <div className="deicing-dashboard">
            <div className="dd-period">
                <strong>Období</strong>
                <div className="dd-segments">
                    {(
                        [
                            ["today", "Dnes"],
                            ["week", "7 dní"],
                            ["month", "Měsíc"],
                            ["firstHalf", "Sezóna leden–červen"],
                            ["secondHalf", "Sezóna červenec–prosinec"],
                            ["custom", "Vlastní"],
                        ] as [Period, string][]
                    ).map(([value, label]) => (
                        <button
                            type="button"
                            key={value}
                            aria-pressed={period === value}
                            onClick={() => selectPeriod(value)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {period === "custom" ? (
                    <>
                        <label>
                            Od{" "}
                            <input
                                type="date"
                                value={from}
                                max={to}
                                onChange={(event) => setFrom(event.target.value)}
                            />
                        </label>
                        <label>
                            Do{" "}
                            <input
                                type="date"
                                value={to}
                                min={from}
                                onChange={(event) => setTo(event.target.value)}
                            />
                        </label>
                    </>
                ) : (
                    <span>
                        {period === "all" ? "Celá doba" : `${dateLabel(from)} – ${dateLabel(to)}`}
                    </span>
                )}
                <button
                    type="button"
                    className="dd-refresh"
                    onClick={() => setRevision((value) => value + 1)}
                    disabled={loading}
                >
                    Obnovit
                </button>
            </div>
            {!valid && (
                <p role="alert">
                    Zadejte platné datum od a do. Začátek nesmí být pozdější než konec.
                </p>
            )}
            {loading && <p role="status">Načítání přehledu…</p>}
            {error && (
                <p role="alert">
                    {error}{" "}
                    <button type="button" onClick={() => setRevision((value) => value + 1)}>
                        Zkusit znovu
                    </button>
                </p>
            )}
            {data && (
                <>
                    {data.total === 0 && (
                        <p role="status">Ve vybraném období nejsou evidované žádné odmrazy.</p>
                    )}
                    <div className="dd-kpis">
                        {forecastFluids.map((fluid, index) => (
                            <section
                                className={`dd-panel dd-kpi dd-fluid-${index}`}
                                key={fluid.name}
                            >
                                <h3>
                                    Zásoba {fluid.name}
                                    <small>
                                        k {new Date(data.updatedAt).toLocaleString("cs-CZ", { hourCycle: "h23" })}
                                    </small>
                                </h3>
                                <div className="dd-value">
                                    {number(fluid.quantity)}
                                    <small> l</small>
                                </div>
                                <p>
                                    Vystačí na cca <b>{number(fluid.remaining)}</b> odmrazů · 30
                                    dní: příjem <b>{number(fluid.receipts)} l</b>
                                </p>
                            </section>
                        ))}
                        <section className="dd-panel dd-kpi">
                            <h3>Počet odmrazů</h3>
                            <div className="dd-value">{number(data.total)}</div>
                            <p className="dd-badge">
                                {data.change == null
                                    ? "Předchozí období bez odmrazů"
                                    : `${data.change > 0 ? "+" : ""}${number(data.change, 1)} % oproti předchozím ${data.days} dnům`}
                            </p>
                            <p>
                                Průměrně <b>{number(data.dailyAverage, 1)}</b> odmrazů denně
                            </p>
                        </section>
                        <section className="dd-panel dd-kpi">
                            <h3>Průměrná doba odmrazu</h3>
                            <div className="dd-value">
                                {number(data.averageMinutes, 1)}
                                <small> min</small>
                            </div>
                            <p>Z časů začátku a konce ({number(data.timedOperations)} odmrazů)</p>
                            <p>
                                Celkový čas odmrazů: <b>{number(data.totalMinutes / 60, 1)} h</b>
                            </p>
                        </section>
                    </div>
                    <div className="dd-two-columns">
                        <section className="dd-panel dd-forecast">
                            <div className="dd-panel-heading">
                                <h3>Odhad dalších odmrazů</h3>
                                <div className="dd-segments dd-forecast-segments" role="group" aria-label="Základ odhadu">
                                    {([
                                        ["recent", "Posledních 30 dní"],
                                        ["selected", "Vybrané období"],
                                        ["all", "Celá doba"],
                                    ] as const).map(([value, label]) => (
                                        <button
                                            type="button"
                                            key={value}
                                            aria-pressed={forecast === value}
                                            onClick={() => setForecast(value)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="dd-value dd-forecast-value">
                                {data.forecast.remaining == null
                                    ? "—"
                                    : `~${number(data.forecast.remaining)}`}
                            </div>
                            <p>
                                {data.forecast.limiting ? (
                                    <>
                                        odmrazů do vyčerpání · omezuje{" "}
                                        <b>{data.forecast.limiting}</b>
                                    </>
                                ) : (
                                    "Pro odhad chybí spotřeba odmrazů ve zvoleném základu."
                                )}
                            </p>
                            <div className="dd-days">≈ {number(data.forecast.days)} dní</div>
                            <p>
                                při {number(data.forecast.dailyRate, 1)} odmrazech denně · vyčerpání{" "}
                                {data.forecast.depletion ? dateLabel(data.forecast.depletion) : "—"}
                            </p>
                            {forecastFluids.map((fluid, index) => (
                                <div className={`dd-estimate dd-fluid-${index}`} key={fluid.name}>
                                    <div>
                                        {fluid.name} · Ø {number(fluid.forecastAverage, 1)} l /
                                        odmraz <b>{number(fluid.remaining)}</b>
                                    </div>
                                    <progress
                                        aria-label={`Odhad ${fluid.name}`}
                                        value={fluid.remaining ?? 0}
                                        max={Math.max(
                                            1,
                                            ...forecastFluids.map((row) => row.remaining ?? 0),
                                        )}
                                    />
                                </div>
                            ))}
                            {data.forecast.orderBy && (
                                <p className="dd-order">
                                    Objednat {data.forecast.limiting} nejpozději{" "}
                                    <b>{dateLabel(data.forecast.orderBy)}</b> (dodací lhůta 14 dní)
                                </p>
                            )}
                        </section>
                        <section className="dd-panel">
                            <div className="dd-panel-heading">
                                <h3>Celková spotřeba za období</h3>
                                <b>{number(data.fluids.reduce((a, row) => a + row.total, 0))} l</b>
                            </div>
                            {data.fluids.map((fluid, index) => (
                                <div
                                    className={`dd-consumption dd-fluid-${index}`}
                                    key={fluid.name}
                                >
                                    <div>
                                        <b>{fluid.name}</b>
                                        <b>{number(fluid.total)} l</b>
                                    </div>
                                    <div className="dd-consumption-track">
                                        <span
                                            style={{
                                                width: `${fluid.total ? (fluid.consumption / fluid.total) * 100 : 0}%`,
                                            }}
                                        />
                                    </div>
                                    <p>
                                        Odmrazy: <b>{number(fluid.consumption)} l</b> ·
                                        Technologické odstřiky: <b>{number(fluid.tech)} l</b> (
                                        {number(
                                            fluid.total ? (fluid.tech / fluid.total) * 100 : 0,
                                            1,
                                        )}{" "}
                                        %)
                                    </p>
                                </div>
                            ))}
                            <p>
                                ■ Odmrazy　
                                <span className="dd-muted">■ Technologické odstřiky</span>
                            </p>
                        </section>
                    </div>
                    <div className="dd-trend-row">
                        <section className="dd-panel">
                            <div className="dd-panel-heading">
                                <h3>Počet odmrazů po dnech</h3>
                                <b>Σ {number(data.total)}</b>
                            </div>
                            <p>
                                Maximum {number(Math.max(0, ...data.daily.map((row) => row.value)))}{" "}
                                {data.total > 0 &&
                                    `(${dateLabel(data.daily.reduce((a, b) => (a.value >= b.value ? a : b)).date)})`}
                            </p>
                            <div className="dd-trend">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={data.daily}
                                        margin={{ top: 15, right: 15, bottom: 10, left: 0 }}
                                        accessibilityLayer
                                    >
                                        <CartesianGrid vertical={false} stroke="#e4edf3" />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={(date) =>
                                                dateLabel(date).replace(/\d{4}$/, "")
                                            }
                                            minTickGap={28}
                                            tick={{ fontSize: 11 }}
                                        />
                                        <YAxis allowDecimals={false} width={35} />
                                        <Tooltip
                                            labelFormatter={(date) => dateLabel(String(date))}
                                            formatter={(value) => [
                                                number(Number(value)),
                                                "Odmrazy",
                                            ]}
                                        />
                                        <Area
                                            type="linear"
                                            dataKey="value"
                                            stroke="#087fa6"
                                            fill="#e5f0f5"
                                            strokeWidth={2}
                                            dot={
                                                data.days <= 60
                                                    ? { r: 3, fill: "white", strokeWidth: 2 }
                                                    : false
                                            }
                                            isAnimationActive={false}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                        <section className="dd-panel">
                            <h3>Průměrná spotřeba na 1 odmraz</h3>
                            {data.fluids.map((fluid, index) => (
                                <div className={`dd-average dd-fluid-${index}`} key={fluid.name}>
                                    <b>{fluid.name}</b>
                                    <div>
                                        <span>
                                            Vybrané období
                                            <strong>{number(fluid.average, 1)} l</strong>
                                        </span>
                                        <span>
                                            Celá doba
                                            <strong>{number(fluid.allAverage, 1)} l</strong>
                                        </span>
                                    </div>
                                    <p className="dd-comparison">
                                        {fluid.average != null &&
                                        fluid.allAverage != null &&
                                        fluid.allAverage > 0
                                            ? `${number((fluid.average / fluid.allAverage - 1) * 100, 1)} % vs. celá doba`
                                            : "Bez dat pro srovnání"}
                                    </p>
                                </div>
                            ))}
                            <p>Do průměru se počítají jen odmrazy, technologické odstřiky ne.</p>
                        </section>
                    </div>
                    <div className="dd-three-columns">
                        {ranking("Odmrazy podle vozidel", data.vehicles, colors[0])}
                        {ranking("Odmrazy podle stojánek", data.stands, "#3da9d5")}
                        <section className="dd-panel">
                            <h3>Odmrazy podle počasí</h3>
                            {data.weather.length ? (
                                <div className="dd-weather">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart accessibilityLayer>
                                            <Pie
                                                data={data.weather}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius="52%"
                                                outerRadius="75%"
                                                cx="50%"
                                                isAnimationActive={false}
                                            >
                                                {data.weather.map((row, index) => (
                                                    <Cell
                                                        key={row.name}
                                                        fill={colors[index % colors.length]}
                                                    />
                                                ))}
                                                <Label
                                                    content={({ viewBox }) => {
                                                        if (!viewBox || !("cx" in viewBox) || !("cy" in viewBox)) return null;
                                                        return (
                                                            <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" fill="#1b3044">
                                                                <tspan x={viewBox.cx} dy="-0.05em" fontSize={23} fontWeight={700}>
                                                                    {number(data.total)}
                                                                </tspan>
                                                                <tspan x={viewBox.cx} dy="1.5em" fontSize={10} fill="#6d8093">
                                                                    odmrazů
                                                                </tspan>
                                                            </text>
                                                        );
                                                    }}
                                                />
                                            </Pie>
                                            <Tooltip
                                                formatter={(value) => [
                                                    number(Number(value)),
                                                    "Odmrazy",
                                                ]}
                                            />
                                            <Legend
                                                layout="vertical"
                                                align="right"
                                                verticalAlign="middle"
                                                wrapperStyle={{ width: "48%", fontSize: 11 }}
                                                formatter={(name) => {
                                                    const row = data.weather.find(
                                                        (row) => row.name === name,
                                                    );
                                                    return `${name} ${number(data.total ? ((row?.value ?? 0) / data.total) * 100 : 0, 1)} %`;
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <p>Žádné odmrazy v období.</p>
                            )}
                        </section>
                    </div>
                    <div className="dd-two-columns dd-airlines">
                        {airlineRanking("Spotřeba kapaliny I", "fluid1", colors[0])}
                        {airlineRanking("Spotřeba kapaliny II", "fluid2", "#238468")}
                        {airlineRanking("Spotřeba vody", "water", "#3da9d5")}
                        {airlineRanking("Počet odmrazů", "value", colors[0])}
                    </div>
                    <footer>
                        <span>
                            Zdroj: evidence odmrazů a skladové pohyby · aktualizováno{" "}
                            {new Date(data.updatedAt).toLocaleString("cs-CZ", { hourCycle: "h23" })}
                        </span>
                        <div>
                            <button type="button" onClick={() => window.print()}>
                                Tisk / PDF
                            </button>
                            {/* <button type="button" onClick={exportExcel} disabled={exporting}>
                                {exporting ? "Exportuji…" : "Export do Excelu"}
                            </button> */}
                        </div>
                    </footer>
                </>
            )}
            {exportError && <p role="alert">{exportError}</p>}
        </div>
        </>
    );
}
